import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const DEFAULT_PARAMS = {
  channel: 'gray',
  invert: true,
  flatten: false,
  bg_subtract_radius: 50,
  threshold_mode: 'otsu',
  threshold_value: 127,
  adaptive_block: 51,
  adaptive_C: 5,
  opening_radius: 3,
  split_touching: false,
  min_area: 30,
  max_area: 50000,
  min_circularity: 0,
  min_solidity: 0,
  circle_filter: true,
  circle_margin: 0.02,
};

// Frontend mirror of backend `app.services.morph_service` knobs. Source of
// truth for defaults lives there; we keep them in sync manually since this
// dialog is the only consumer.
//
// The dialog renders sliders → debounced POST /gpu/morph_preview → server
// returns a base64 PNG of the threshold mask which we overlay on the frame
// the user is currently viewing. "Run" then triggers /gpu/automask with
// pipeline=morphological + pipeline_kwargs.
export default function MorphThresholdDialog({
  open,
  onClose,
  onRun,
  gpuUrl,
  sessionId,
  frameIdx,
  roi,                       // {x, y, w, h} | null
  frameBitmap,               // ImageBitmap | HTMLImageElement | HTMLCanvasElement (for preview overlay)
}) {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [componentCount, setComponentCount] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Reset on open so each dialog session starts clean. Keep user-supplied
  // ROI but reset slider state to defaults.
  useEffect(() => {
    if (open) {
      setParams(DEFAULT_PARAMS);
      setPreviewSrc(null);
      setPreviewErr(null);
      setComponentCount(null);
    }
  }, [open]);

  const update = useCallback((patch) => {
    setParams((prev) => ({ ...prev, ...patch }));
  }, []);

  const runPreview = useCallback(async (p) => {
    if (!sessionId || !gpuUrl) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setPreviewLoading(true);
    setPreviewErr(null);
    try {
      const resp = await fetch(`${gpuUrl}/morph_preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          frame_idx: frameIdx,
          roi,
          pipeline_kwargs: p,
          preview_max_side: 768,
        }),
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${resp.status}`);
      }
      const j = await resp.json();
      setPreviewSrc(`data:image/png;base64,${j.preview_png_b64}`);
      setComponentCount(j.components);
    } catch (err) {
      if (err.name !== 'AbortError') setPreviewErr(err.message || String(err));
    } finally {
      setPreviewLoading(false);
    }
  }, [gpuUrl, sessionId, frameIdx, roi]);

  // Debounced preview on any param change while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runPreview(params), 250);
    return () => clearTimeout(debounceRef.current);
  }, [open, params, runPreview]);

  const handleRun = () => {
    onRun({ pipeline_kwargs: params });
  };

  const channelOptions = ['gray', 'red', 'green', 'blue'];

  // Snapshot the frame ROI (not the full frame). Backend runs threshold on
  // the cropped ROI region and returns a PNG of those bounds; rendering the
  // full frame underneath produced a misalignment (the dish drifted off
  // the overlay) because the two layers had different aspect ratios.
  const frameDataURL = useMemo(() => {
    if (!frameBitmap || !open) return null;
    try {
      const fw = frameBitmap.width || frameBitmap.naturalWidth || 0;
      const fh = frameBitmap.height || frameBitmap.naturalHeight || 0;
      if (!fw || !fh) return null;
      let sx = 0, sy = 0, sw = fw, sh = fh;
      if (roi && roi.w > 0 && roi.h > 0) {
        sx = Math.max(0, Math.min(fw - 1, Math.round(roi.x)));
        sy = Math.max(0, Math.min(fh - 1, Math.round(roi.y)));
        sw = Math.max(1, Math.min(fw - sx, Math.round(roi.w)));
        sh = Math.max(1, Math.min(fh - sy, Math.round(roi.h)));
      }
      const scale = Math.min(1, 768 / Math.max(sw, sh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(frameBitmap, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch {
      return null;
    }
  }, [frameBitmap, open, roi]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Threshold / Auto-Detect (Classical CV)</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          {/* Sliders panel */}
          <Box sx={{ width: 340, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                label="Channel"
                value={params.channel}
                onChange={(e) => update({ channel: e.target.value })}
              >
                {channelOptions.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch
                checked={params.invert}
                onChange={(e) => update({ invert: e.target.checked })}
              />}
              label={<Typography variant="body2">Dark colonies on light background</Typography>}
            />

            <Box>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Background subtraction radius (px) — 0 disables
              </Typography>
              <Slider
                size="small"
                value={params.bg_subtract_radius}
                onChange={(_, v) => update({ bg_subtract_radius: v })}
                min={0} max={200} step={5}
                valueLabelDisplay="auto"
              />
            </Box>

            <FormControlLabel
              control={<Switch
                checked={params.flatten}
                onChange={(e) => update({ flatten: e.target.checked })}
              />}
              label={<Typography variant="body2">Flatten uneven lighting (flat-field)</Typography>}
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Threshold mode</InputLabel>
              <Select
                label="Threshold mode"
                value={params.threshold_mode}
                onChange={(e) => update({ threshold_mode: e.target.value })}
              >
                <MenuItem value="otsu">Otsu (global auto)</MenuItem>
                <MenuItem value="adaptive">Adaptive (local, uneven lighting)</MenuItem>
                <MenuItem value="mser">Multi-level MSER (low contrast)</MenuItem>
                <MenuItem value="manual">Manual (fixed cut)</MenuItem>
              </Select>
            </FormControl>

            {params.threshold_mode === 'manual' && (
              <Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Threshold value (0–255)
                </Typography>
                <Slider
                  size="small"
                  value={params.threshold_value}
                  onChange={(_, v) => update({ threshold_value: v })}
                  min={0} max={255} step={1}
                  valueLabelDisplay="auto"
                />
              </Box>
            )}

            {params.threshold_mode === 'adaptive' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Block size (odd)
                  </Typography>
                  <Slider
                    size="small"
                    value={params.adaptive_block}
                    onChange={(_, v) => update({ adaptive_block: v % 2 === 0 ? v + 1 : v })}
                    min={3} max={151} step={2}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Sensitivity (C)
                  </Typography>
                  <Slider
                    size="small"
                    value={params.adaptive_C}
                    onChange={(_, v) => update({ adaptive_C: v })}
                    min={-20} max={20} step={1}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Box>
            )}

            <FormControlLabel
              control={<Switch
                checked={params.split_touching}
                onChange={(e) => update({ split_touching: e.target.checked })}
              />}
              label={<Typography variant="body2">Split touching colonies (watershed)</Typography>}
            />

            <Box>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Opening radius (px) — removes specks
              </Typography>
              <Slider
                size="small"
                value={params.opening_radius}
                onChange={(_, v) => update({ opening_radius: v })}
                min={0} max={15} step={1}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Min area (px)"
                value={params.min_area}
                onChange={(e) => update({ min_area: Math.max(1, Number(e.target.value) || 1) })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="number"
                label="Max area (px)"
                value={params.max_area}
                onChange={(e) => update({ max_area: Math.max(2, Number(e.target.value) || 2) })}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Min circularity — 0 off, higher drops arcs/scratches
              </Typography>
              <Slider
                size="small"
                value={params.min_circularity}
                onChange={(_, v) => update({ min_circularity: v })}
                min={0} max={1} step={0.05}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Min solidity — 0 off, higher drops ragged blobs
              </Typography>
              <Slider
                size="small"
                value={params.min_solidity}
                onChange={(_, v) => update({ min_solidity: v })}
                min={0} max={1} step={0.05}
                valueLabelDisplay="auto"
              />
            </Box>

            {roi && (
              <FormControlLabel
                control={<Switch
                  checked={params.circle_filter}
                  onChange={(e) => update({ circle_filter: e.target.checked })}
                />}
                label={<Typography variant="body2">Filter outside dish circle</Typography>}
              />
            )}

            <Box sx={{ pt: 1, borderTop: '1px solid #333' }}>
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                Components in preview: {componentCount ?? '—'}
                {previewLoading && <CircularProgress size={10} sx={{ ml: 1 }} />}
              </Typography>
              {previewErr && (
                <Typography variant="caption" sx={{ color: '#e57373', display: 'block' }}>
                  Preview error: {previewErr}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Preview pane */}
          <Box
            sx={{
              flex: 1,
              minHeight: 480,
              position: 'relative',
              border: '1px solid #333',
              bgcolor: '#111',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {frameDataURL && (
              <img
                src={frameDataURL}
                alt="frame"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', position: 'absolute', inset: 0, margin: 'auto' }}
              />
            )}
            {previewSrc && (
              <img
                src={previewSrc}
                alt="threshold preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  position: 'absolute',
                  inset: 0,
                  margin: 'auto',
                  opacity: 0.55,
                  mixBlendMode: 'screen',
                  pointerEvents: 'none',
                }}
              />
            )}
            {!previewSrc && !previewLoading && !previewErr && (
              <Typography variant="body2" sx={{ color: '#666' }}>
                Adjust sliders to generate preview.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRun}
          disabled={previewLoading || previewErr != null}
        >
          Run on Full Resolution
        </Button>
      </DialogActions>
    </Dialog>
  );
}
